import { BuiltInFont } from 'web';
import { posthog } from '@/providers/posthog';
import {
  FF_HIBOB_FONTS,
  FF_MAAR_FONTS,
  FF_ALEPH_FONTS,
  FF_RAKUTEN_FONTS,
  FF_SCRIBOS_FONTS,
  FF_DEPOSCO_FONTS,
  FF_EMPIK_FONTS,
  FF_BARKBOX_FONTS,
  FF_KOMFI_FONTS,
  FF_VMFOOTWEAR_FONTS,
  FF_JOURNEYFURTHER_FONTS,
  FF_NOBEARS_FONTS,
  FF_NVIDIA_FONTS,
  FF_OPERA_FONTS,
  FF_SAMURAI_FONTS,
  FF_VOLVO_FONTS,
  FF_SAMY_FONTS,
  FF_BUSUU_FONTS,
  FF_SPAFAX_FONTS,
  FF_TEAM5PM_FONTS,
  FF_LLBEAN_FONTS,
  FF_A16Z_FONTS,
  FF_PUNKLE_FONTS,
  FF_PREDATOR_GROUP_FONTS,
  FF_STRATEYRANT_FONTS,
  FF_DUNLOP_FONTS,
  FF_AND_OR_FONTS,
  FF_LINDEX_FONTS,
  FF_JAN_GERICKE_FONTS,
  FF_METROPOLIS_FONTS,
  FF_AROSOLUCIONES_FONTS,
  FF_MINTHC_FONTS,
  FF_WOLFE_FONTS,
  FF_META_CREATIVE_X_FONTS,
  FF_POLYMATHTEXT_FONTS,
  FF_JOURNEY_FONTS,
  FF_ENCENDER_FONTS,
  FF_EUCLID_SQUARE_FONTS,
  FF_OPG_FONTS,
  FF_OUTBREAKER_FONTS,
  FF_VIRGIN_SCRIPTY_FONTS,
  FF_VACIER_FONTS,
  FF_45AI_FONTS,
  FF_UROSKRA77_FONTS,
} from '@/consts/featureFlags';

interface BaseFontConfig {
  value: BuiltInFont;
  label: string;
}

interface CompanyFontConfig extends BaseFontConfig {
  featureFlag: string;
}

// Base fonts available to all users
export const BASE_FONTS: readonly BaseFontConfig[] = [
  { value: 'Nunito', label: 'Nunito' },
  { value: 'DMSans', label: 'DM Sans' },
  { value: 'Caveat', label: 'Caveat' },
  { value: 'CrimsonText', label: 'Crimson Text' },
  { value: 'EBGaramond', label: 'EB Garamond' },
  { value: 'FiraCode', label: 'Fira Code' },
  { value: 'IBMPlexSans', label: 'IBM Plex Sans' },
  { value: 'Inter', label: 'Inter' },
  { value: 'InterTight', label: 'Inter Tight' },
  { value: 'JetBrainsMono', label: 'JetBrains Mono' },
  { value: 'Pacifico', label: 'Pacifico' },
  { value: 'Lobster', label: 'Lobster' },
  { value: 'Karla', label: 'Karla' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'SpaceMono', label: 'Space Mono' },
  { value: 'RobotoSlab', label: 'Roboto Slab' },
  { value: 'PlayfairDisplay', label: 'Playfair Display' },
  { value: 'LibreBaskerville', label: 'Libre Baskerville' },
  { value: 'Roboto', label: 'Roboto' },
  { value: 'SpaceGrotesk', label: 'Space Grotesk' },
  { value: 'SourceCodePro', label: 'Source Code Pro' },
  { value: 'Sacramento', label: 'Sacramento' },
  { value: 'WorkSans', label: 'Work Sans' },
  { value: 'OpenSans', label: 'Open Sans' },
  { value: 'Outfit', label: 'Outfit' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Merriweather', label: 'Merriweather' },
  { value: 'Kanit', label: 'Kanit' },
  { value: 'JosefinSans', label: 'Josefin Sans' },
  { value: 'Figtree', label: 'Figtree' },
  { value: 'Arimo', label: 'Arimo' },
  { value: 'Arapey', label: 'Arapey' },
  { value: 'BarlowCondensed', label: 'Barlow Condensed' },
  { value: 'SofiaSansExtraCondensed', label: 'Sofia Sans Extra Condensed' },
  { value: 'BebasNeue', label: 'Bebas Neue' },
  { value: 'SourceSans3', label: 'Source Sans 3' },
  { value: 'NotoSans', label: 'Noto Sans' },
  { value: 'NotoSansJP', label: 'Noto Sans JP' },
  { value: 'PlusJakartaSans', label: 'Plus Jakarta Sans' },
  { value: 'Rubik', label: 'Rubik' },
];

export const BASE_BUILT_IN_FONT_KEY_SET: ReadonlySet<BuiltInFont> = new Set(BASE_FONTS.map((f) => f.value));

// Company-specific fonts
const COMPANY_FONTS: CompanyFontConfig[] = [
  // Hibob fonts
  { value: 'Champion', label: 'Champion', featureFlag: FF_HIBOB_FONTS },
  { value: 'Sentinel', label: 'Sentinel', featureFlag: FF_HIBOB_FONTS },
  { value: 'GothamSSm', label: 'Gotham', featureFlag: FF_HIBOB_FONTS },
  // Maar fonts
  { value: 'NaturaDisplay', label: 'Natura Display', featureFlag: FF_MAAR_FONTS },
  { value: 'VersosTest', label: 'Versos Test', featureFlag: FF_MAAR_FONTS },
  { value: 'NaturaEKOS', label: 'Natura EKOS', featureFlag: FF_MAAR_FONTS },

  // Aleph fonts
  { value: 'Altform', label: 'Altform', featureFlag: FF_ALEPH_FONTS },

  // Rakuten fonts
  { value: 'RakutenSans', label: 'Rakuten Sans', featureFlag: FF_RAKUTEN_FONTS },
  // Scribos fonts
  { value: 'Arial', label: 'Arial', featureFlag: FF_SCRIBOS_FONTS },
  { value: 'ArialNarrow', label: 'Arial Narrow', featureFlag: FF_SCRIBOS_FONTS },
  { value: 'ArialRounded', label: 'Arial Rounded', featureFlag: FF_SCRIBOS_FONTS },
  { value: 'Helvetica', label: 'Helvetica', featureFlag: FF_SCRIBOS_FONTS },
  { value: 'HelveticaNeueLTPro', label: 'Helvetica Neue LT Pro', featureFlag: FF_SCRIBOS_FONTS },
  { value: 'OCRB0', label: 'OCR B0', featureFlag: FF_SCRIBOS_FONTS },
  // Deposco fonts
  { value: 'Gotham', label: 'Gotham', featureFlag: FF_DEPOSCO_FONTS },
  { value: 'GothamCondensed', label: 'Gotham Condensed', featureFlag: FF_DEPOSCO_FONTS },
  { value: 'GothamNarrow', label: 'Gotham Narrow', featureFlag: FF_DEPOSCO_FONTS },
  { value: 'BebasNeueBold', label: 'Bebas Neue Bold', featureFlag: FF_DEPOSCO_FONTS },
  { value: 'DegularMono', label: 'Degular Mono', featureFlag: FF_DEPOSCO_FONTS },
  { value: 'Futura', label: 'Futura', featureFlag: FF_DEPOSCO_FONTS },
  { value: 'PermanentMarker', label: 'Permanent Marker', featureFlag: FF_DEPOSCO_FONTS },
  // Empik fonts
  { value: 'EmpikPro', label: 'Empik Pro', featureFlag: FF_EMPIK_FONTS },
  // Barkbox fonts
  { value: 'ABCGintoNordCondensed', label: 'ABC Ginto Nord Condensed', featureFlag: FF_BARKBOX_FONTS },
  { value: 'Pancho', label: 'Pancho', featureFlag: FF_BARKBOX_FONTS },
  { value: 'PanchoW03', label: 'Pancho W03', featureFlag: FF_BARKBOX_FONTS },
  // Jan Gericke fonts
  { value: 'Swanstone', label: 'Swanstone', featureFlag: FF_JAN_GERICKE_FONTS },
  // Komfi fonts
  { value: 'Satoshi', label: 'Satoshi', featureFlag: FF_KOMFI_FONTS },
  // VMFootwear fonts
  { value: 'MyriadPro', label: 'Myriad Pro', featureFlag: FF_VMFOOTWEAR_FONTS },
  { value: 'MyriadProCond', label: 'Myriad Pro Condensed', featureFlag: FF_VMFOOTWEAR_FONTS },
  { value: 'MyriadProSemiCn', label: 'Myriad Pro Semi Condensed', featureFlag: FF_VMFOOTWEAR_FONTS },
  { value: 'MyriadProSemiExt', label: 'Myriad Pro Semi Extended', featureFlag: FF_VMFOOTWEAR_FONTS },
  // Journey Further fonts
  { value: 'BarbourSans', label: 'Barbour Sans', featureFlag: FF_JOURNEYFURTHER_FONTS },
  { value: 'BarbourSerif', label: 'Barbour Serif', featureFlag: FF_JOURNEYFURTHER_FONTS },
  // Nobears fonts
  { value: 'Monroe', label: 'Monroe', featureFlag: FF_NOBEARS_FONTS },
  { value: 'Manrope3', label: 'Manrope3', featureFlag: FF_NOBEARS_FONTS },
  { value: 'HelloBunny', label: 'Hello Bunny', featureFlag: FF_NOBEARS_FONTS },
  { value: 'Filsonsoft', label: 'Filsonsoft', featureFlag: FF_NOBEARS_FONTS },
  { value: 'CeraPro', label: 'Cera Pro', featureFlag: FF_NOBEARS_FONTS },
  // NVIDIA fonts
  { value: 'NVIDIASans', label: 'NVIDIA Sans', featureFlag: FF_NVIDIA_FONTS },
  // Opera fonts
  { value: 'BeVietnamPro', label: 'Be Vietnam Pro', featureFlag: FF_OPERA_FONTS },
  // Samur-ai fonts
  { value: 'GoogleSans', label: 'Google Sans', featureFlag: FF_SAMURAI_FONTS },
  // Volvo fonts
  { value: 'VolvoNovum', label: 'Volvo Novum', featureFlag: FF_VOLVO_FONTS },
  { value: 'VolvoBroadProDigital', label: 'Volvo Broad Pro Digital', featureFlag: FF_VOLVO_FONTS },
  // Samy fonts
  { value: 'McLarenBespoke', label: 'McLaren Bespoke', featureFlag: FF_SAMY_FONTS },
  // Busuu fonts
  { value: 'BwNistaGrot', label: 'Bw Nista Grot', featureFlag: FF_BUSUU_FONTS },
  // Spafax fonts
  { value: 'Emirates', label: 'Emirates', featureFlag: FF_SPAFAX_FONTS },
  { value: 'HeroicCondensed', label: 'Heroic Condensed', featureFlag: FF_SPAFAX_FONTS },
  { value: 'HelveticaNeueLTProV2', label: 'Helvetica Neue LT Pro', featureFlag: FF_SPAFAX_FONTS },
  // Team5pm fonts
  { value: 'Avantt', label: 'Avantt', featureFlag: FF_TEAM5PM_FONTS },
  { value: 'FKScreamer', label: 'FK Screamer', featureFlag: FF_TEAM5PM_FONTS },
  // LLBean fonts
  { value: 'BeanCheltOT', label: 'Bean Chelt OT', featureFlag: FF_LLBEAN_FONTS },
  { value: 'FranklinGothic', label: 'Franklin Gothic', featureFlag: FF_LLBEAN_FONTS },
  { value: 'Gotham', label: 'Gotham', featureFlag: FF_LLBEAN_FONTS },
  // a16z fonts
  { value: 'ABCFavorit', label: 'ABC Favorit', featureFlag: FF_A16Z_FONTS },
  { value: 'ABCFavoritMono', label: 'ABC Favorit Mono', featureFlag: FF_A16Z_FONTS },
  { value: 'DomaineDisplay', label: 'Domaine Display', featureFlag: FF_A16Z_FONTS },
  { value: 'DomaineDisplayCondensed', label: 'Domaine Display Condensed', featureFlag: FF_A16Z_FONTS },
  { value: 'DomaineDisplayNarrow', label: 'Domaine Display Narrow', featureFlag: FF_A16Z_FONTS },
  { value: 'DomaineSansText', label: 'Domaine Sans Text', featureFlag: FF_A16Z_FONTS },
  { value: 'DomaineText', label: 'Domaine Text', featureFlag: FF_A16Z_FONTS },
  { value: 'MessinaSans', label: 'Messina Sans', featureFlag: FF_A16Z_FONTS },
  { value: 'OrpheusPro', label: 'Orpheus Pro', featureFlag: FF_A16Z_FONTS },
  { value: 'SixteenMono', label: 'Sixteen Mono', featureFlag: FF_A16Z_FONTS },
  // Punkle Agency fonts
  { value: 'HelveticaNeue', label: 'Helvetica Neue', featureFlag: FF_PUNKLE_FONTS },
  { value: 'HelveticaNeueCondensed', label: 'Helvetica Neue Condensed', featureFlag: FF_PUNKLE_FONTS },
  // Predator Group fonts
  { value: 'DDCHardware', label: 'DDC Hardware', featureFlag: FF_PREDATOR_GROUP_FONTS },
  { value: 'DDCHardwareCondensed', label: 'DDC Hardware Condensed', featureFlag: FF_PREDATOR_GROUP_FONTS },
  { value: 'DDCHardwareCompressed', label: 'DDC Hardware Compressed', featureFlag: FF_PREDATOR_GROUP_FONTS },
  { value: 'FuturaPT', label: 'Futura PT', featureFlag: FF_PREDATOR_GROUP_FONTS },
  { value: 'FuturaPTCond', label: 'Futura PT Condensed', featureFlag: FF_PREDATOR_GROUP_FONTS },
  // StrategyRant fonts
  { value: 'SundayBloom', label: 'Sunday Bloom', featureFlag: FF_STRATEYRANT_FONTS },
  // Dunlop fonts
  { value: 'DINPro', label: 'DIN Pro', featureFlag: FF_DUNLOP_FONTS },
  { value: 'DINCondensed', label: 'DIN Condensed', featureFlag: FF_DUNLOP_FONTS },
  // and-or fonts
  { value: 'TTCommonsPro', label: 'TT Commons Pro', featureFlag: FF_AND_OR_FONTS },
  { value: 'TTCommonsProCompact', label: 'TT Commons Pro Compact', featureFlag: FF_AND_OR_FONTS },
  { value: 'TTCommonsProCondensed', label: 'TT Commons Pro Condensed', featureFlag: FF_AND_OR_FONTS },
  { value: 'TTCommonsProExpanded', label: 'TT Commons Pro Expanded', featureFlag: FF_AND_OR_FONTS },
  { value: 'TTCommonsProMono', label: 'TT Commons Pro Mono', featureFlag: FF_AND_OR_FONTS },
  // Lindex fonts
  { value: 'LindexSans', label: 'Lindex Sans', featureFlag: FF_LINDEX_FONTS },
  { value: 'LindexSerif', label: 'Lindex Serif', featureFlag: FF_LINDEX_FONTS },
  // Metropolis fonts
  { value: 'Metropolis', label: 'Metropolis', featureFlag: FF_METROPOLIS_FONTS },
  // Arosoluciones fonts
  { value: 'Fredoka', label: 'Fredoka', featureFlag: FF_AROSOLUCIONES_FONTS },
  { value: 'Moon', label: 'Moon', featureFlag: FF_AROSOLUCIONES_FONTS },
  { value: 'Reef', label: 'Reef', featureFlag: FF_AROSOLUCIONES_FONTS },
  // Minthc fonts
  { value: 'Aeonik', label: 'Aeonik', featureFlag: FF_MINTHC_FONTS },
  // Wolfe Agency fonts
  { value: 'Visby', label: 'Visby', featureFlag: FF_WOLFE_FONTS },
  // Polymathtext fonts
  { value: 'PolymathText', label: 'Polymath Text', featureFlag: FF_POLYMATHTEXT_FONTS },
  // Journey fonts
  { value: 'AwesomeSerif', label: 'Awesome Serif', featureFlag: FF_JOURNEY_FONTS },
  { value: 'AwesomeSerifTall', label: 'Awesome Serif Tall', featureFlag: FF_JOURNEY_FONTS },
  { value: 'AwesomeSerifExtraTall', label: 'Awesome Serif Extra Tall', featureFlag: FF_JOURNEY_FONTS },
  { value: 'Fellix', label: 'Fellix', featureFlag: FF_JOURNEY_FONTS },
  { value: 'TAYDreamboat', label: 'TAY Dreamboat', featureFlag: FF_JOURNEY_FONTS },
  // Meta Creative X fonts
  { value: 'AvenirNext', label: 'Avenir Next', featureFlag: FF_META_CREATIVE_X_FONTS },
  { value: 'GaramondNarrow', label: 'Garamond Narrow', featureFlag: FF_META_CREATIVE_X_FONTS },
  { value: 'HelveticaNeueWorld', label: 'Helvetica Neue World', featureFlag: FF_META_CREATIVE_X_FONTS },
  { value: 'Lato', label: 'Lato', featureFlag: FF_META_CREATIVE_X_FONTS },
  { value: 'Optimistic', label: 'Optimistic', featureFlag: FF_META_CREATIVE_X_FONTS },
  { value: 'OptimisticArbc', label: 'Optimistic Arbc', featureFlag: FF_META_CREATIVE_X_FONTS },
  { value: 'OptimisticCyrl', label: 'Optimistic Cyrl', featureFlag: FF_META_CREATIVE_X_FONTS },
  { value: 'OptimisticDeva', label: 'Optimistic Deva', featureFlag: FF_META_CREATIVE_X_FONTS },
  { value: 'OptimisticJP', label: 'Optimistic JP', featureFlag: FF_META_CREATIVE_X_FONTS },
  { value: 'OptimisticViet', label: 'Optimistic Viet', featureFlag: FF_META_CREATIVE_X_FONTS },
  { value: 'RayBanSans', label: 'Ray-Ban Sans', featureFlag: FF_META_CREATIVE_X_FONTS },
  // Encender fonts
  { value: 'KiaSignature', label: 'Kia Signature', featureFlag: FF_ENCENDER_FONTS },
  // Euclid Square fonts
  { value: 'EuclidSquare', label: 'Euclid Square', featureFlag: FF_EUCLID_SQUARE_FONTS },
  // OPG fonts
  { value: 'Exo', label: 'Exo', featureFlag: FF_OPG_FONTS },
  { value: 'SairaStencilOne', label: 'Saira Stencil One', featureFlag: FF_OPG_FONTS },
  { value: 'StardosStencil', label: 'Stardos Stencil', featureFlag: FF_OPG_FONTS },
  { value: 'StickNoBills', label: 'Stick No Bills', featureFlag: FF_OPG_FONTS },
  // Outbreaker fonts
  { value: 'GoogleSansOutbreaker', label: 'Google Sans', featureFlag: FF_OUTBREAKER_FONTS },
  // virgin.com fonts
  { value: 'VirginScripty', label: 'Virgin Scripty', featureFlag: FF_VIRGIN_SCRIPTY_FONTS },
  // Vacier fonts
  { value: 'Termina', label: 'Termina', featureFlag: FF_VACIER_FONTS },
  // 45ai fonts
  { value: 'NeueFrutigerWorld', label: 'Neue Frutiger World', featureFlag: FF_45AI_FONTS },
  // uroskra77 fonts
  { value: 'A1Sans', label: 'A1 Sans', featureFlag: FF_UROSKRA77_FONTS },
  { value: 'A1Serif', label: 'A1 Serif', featureFlag: FF_UROSKRA77_FONTS },
  { value: 'A1SerifOutline', label: 'A1 Serif Outline', featureFlag: FF_UROSKRA77_FONTS },
];

function FontsList() {
  const availableCompanyFonts = COMPANY_FONTS.filter(
    (font) => !font.featureFlag || posthog.isFeatureEnabled(font.featureFlag),
  );

  return [...BASE_FONTS, ...availableCompanyFonts].sort((a, b) => a.label.localeCompare(b.label));
}

export default FontsList;
